/**
 * Edit or delete a note, for its writer or the project's owner (vfs-service marks `canChange`). Used by the Notes list
 * (Handoff.jsx, handoff notes) and Messages (notes between agents and people). The notes live with the project on its
 * owner's pod, so every collaborator sees the change; a deleted note is gone for everyone, agents included.
 *
 * NoteChange({ fields: [{ key, label }], values, onSave(values) -> Promise, onDelete() -> Promise, onChanged?, noun? }):
 * "Edit · Delete" links; Edit opens a textarea per field with Save / Cancel; Delete asks once inline.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './Collab.css';

const MAX = 2000;

export default function NoteChange({ fields, values, onSave, onDelete, onChanged = null, noun = 'note' }) {
  const [mode, setMode] = useState(null); // null | 'edit' | 'delete'
  const [draft, setDraft] = useState(values);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async(action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setMode(null);
      onChanged?.();
    } catch (err) {
      setError(err?.message || `The ${noun} could not be changed.`);
    } finally {
      setBusy(false);
    }
  };
  if (mode === 'edit') {
    const empty = fields.every((f) => !String(draft[f.key] || '').trim());
    return (
      <div className="collab-note-change" data-testid="note-edit">
        {fields.map((f) => (
          <label className="collab-field" key={f.key}>
            <span>{f.label}</span>
            <textarea data-testid={`note-edit-${f.key}`} maxLength={MAX} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} value={draft[f.key] || ''} />
          </label>
        ))}
        {error && <div className="collab-error collab-small" role="alert">{error}</div>}
        <div className="collab-handoff-helpers">
          <button className="collab-btn" data-testid="note-edit-save" disabled={busy || empty} onClick={() => run(() => onSave(draft))} type="button">Save</button>
          <button className="collab-link collab-small" disabled={busy} onClick={() => setMode(null)} type="button">Cancel</button>
        </div>
      </div>
    );
  }
  if (mode === 'delete') {
    return (
      <div className="collab-note-change collab-small" data-testid="note-delete-confirm" role="group">
        <span>Delete this {noun} for everyone?</span>{' '}
        <button className="collab-link collab-error" data-testid="note-delete-yes" disabled={busy} onClick={() => run(onDelete)} type="button">Delete</button>{' · '}
        <button className="collab-link" disabled={busy} onClick={() => setMode(null)} type="button">Keep it</button>
        {error && <div className="collab-error" role="alert">{error}</div>}
      </div>
    );
  }
  return (
    <span className="collab-note-change collab-small">
      <button className="collab-link" data-testid="note-edit-open" onClick={() => { setDraft(values); setError(null); setMode('edit'); }} type="button">Edit</button>{' · '}
      <button className="collab-link" data-testid="note-delete" onClick={() => { setError(null); setMode('delete'); }} type="button">Delete</button>
    </span>
  );
}

NoteChange.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  noun: PropTypes.string,
  onChanged: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};
